import { BrowserRouter } from "react-router-dom";
import { AssistantProvider } from "@/features/tutor/context/AssistantContext.jsx";
import PingComponent from "@/features/tutor/components/PingComponent.jsx";
import { useEffect, useRef } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import AppRoutes from "@/routes";
import { SupportSessionBanner } from "@/components/SupportSessionBanner";
import ErrorBoundary from "./utils/ErrorBoudary";
import {
  identifyMixpanelUser,
  mixpanelEvents,
  trackSessionEnd,
  trackLessonEnd,
  trackSessionStart,
  resetSessionId,
} from "./utils/mixpanel-utils";
import useInactivityTimer from "./hooks/useInactivityTimer";
import { useAuthStore } from "./store/useAuthStore";

const App = () => {
  const { user } = useAuthStore();

  useEffect(() => {
    // Kept on MathJax (not et-ui): teacher slide HTML uses MathJax-only features KaTeX can't render.
    window.MathJax = {
      tex: {
        inlineMath: [
          ["$", "$"],
          ["\\(", "\\)"],
        ],
        displayMath: [
          ["$$", "$$"],
          ["\\[", "\\]"],
        ],
        macros: {
          "\\mathdefault": "\\mathrm",
        },
      },
    };

    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const generateUserHash = () => {
    const timestamp = Date.now().toString();
    const random = Math.random().toString();
    const userAgent = navigator.userAgent;

    // Combine values to create unique string
    const uniqueString = `${timestamp}-${random}-${userAgent}`;

    // Create SHA1 hash
    return crypto.subtle
      .digest("SHA-1", new TextEncoder().encode(uniqueString))
      .then((hashBuffer) => {
        // Convert buffer to hex string
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
        return hashHex;
      });
  };

  useEffect(() => {
    // Usage:
    const createUserHash = async () => {
      const hash = await generateUserHash();
      sessionStorage.setItem("demoClientId", hash);
      return hash;
    };
    if (!sessionStorage.getItem("demoClientId")) {
      createUserHash();
    }
  }, []);

  useEffect(() => {
    if (user) {
      trackSessionStart({});

      const handleBeforeUnload = () => {
        trackSessionEnd(
          {
            reason: "session_closed",
          },
          { transport: "sendBeacon" },
        );
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [user]);

  const handleInactivity = () => {
    trackLessonEnd({
      reason: "session_inactive",
    });

    trackSessionEnd({
      reason: "session_inactive",
    });
    resetSessionId();
  };

  useInactivityTimer(handleInactivity);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AssistantProvider>
          <PingComponent />
          {/* Above the router so it shows on every route, never dismissable. */}
          <SupportSessionBanner />
          <AppRoutes />
        </AssistantProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
